import React from 'react';
import axiosClient from '../../services/axiosClient';
import apiClient from '../services/apiService.js';
import API_CONFIG from '../config/api.js';

export const STATIC_CATEGORIES = [
  'Fournitures courantes',
  'Travaux BTP & Génie civil',
  'Prestations intellectuelles',
  'BTP',
  'Informatique & Télécoms',
  'Indépendants & Freelance',
  'Services financiers',
  'Banque',
  'Sécurité & Sureté',
  'Formation',
  'Ressources humaines',
  'Immobilier',
  'Marketing & Communication',
  'Garderie',
  'Médecine & Santé',
  'Genre et Inclusion Sociale',
  'Tourisme & Restaurants',
  'Transport et Logistique',
  'Service public',
  'Agro-alimentaire',
  'Elevage',
  'Electricité',
  'Prestation de services',
];

// Simple in-memory cache with in-flight deduction
let cacheData = null; // Array<{id?: any, name: string}>
let cacheAt = 0;
let inFlight = null; // Promise
const TTL_MS = 5 * 60 * 1000; // 5 minutes

function normalizeCategories(raw) {
  let data = raw?.data?.data || raw?.data?.result || raw?.data || [];
  if (!Array.isArray(data)) data = Array.isArray(data.data) ? data.data : [];
  return data.map((c, i) => ({
    id: c?.id ?? c?.category_id ?? c?.value ?? i,
    name: c?.name ?? c?.title ?? c?.label ?? `Catégorie ${i + 1}`,
  }));
}

export async function getCategoriesCached(signal) {
  const now = Date.now();
  if (cacheData && (now - cacheAt) < TTL_MS) {
    return cacheData;
  }
  if (inFlight) return inFlight;
  // Prefer shared apiClient (handles baseURL, headers, auth, 401)
  inFlight = apiClient.get(API_CONFIG.ENDPOINTS.CATEGORIES.LIST, {
    // ensure a reasonable timeout
    timeout: 10000,
    headers: {
      Accept: 'application/json',
      'X-Skip-Auth': 'true',
    },
  }).then((res) => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[useCategories] categories fetched ok');
    }
    const mapped = normalizeCategories(res);
    cacheData = mapped;
    cacheAt = Date.now();
    inFlight = null;
    return mapped;
  }).catch((err) => {
    inFlight = null;
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('[useCategories] fetch error', err);
    }
    throw err;
  });
  return inFlight;
}

export function useCategories(options = { fallbackStatic: true }) {
  const { fallbackStatic = true } = options;
  const [categories, setCategories] = React.useState(() => cacheData || []);
  const [loading, setLoading] = React.useState(!cacheData);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let mounted = true;
    const controller = new AbortController();
    (async () => {
      try {
        setLoading(!cacheData);
        const data = await getCategoriesCached(controller.signal);
        if (!mounted) return;
        setCategories(data);
        setError('');
      } catch (e) {
        if (axiosClient.isCancel?.(e) || e?.name === 'CanceledError' || e?.name === 'AbortError') return;
        if (!mounted) return;
        setError('Impossible de charger les catégories');
        if (fallbackStatic) {
          setCategories(STATIC_CATEGORIES.map((n, i) => ({ id: i, name: n })));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; controller.abort(); };
  }, [fallbackStatic]);

  return { categories, loading, error };
}
